import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
// Through the index, so page tests can stub the layout.
import { DashboardLayout, Card, Button } from '.';
import { ProjectsListTable, type ProjectsListColumn } from './ProjectsListTable';
import { Breadcrumb } from './Breadcrumb';
import { useColumnOrder } from '../hooks/useColumnOrder';
import { adminApi } from '../services/api';
import type { CompanyInvitationStatus } from '../types';

/** The fields every invitation row has, contractor or client. */
export interface AdminInvitationRow {
  id: string;
  customerName?: string | null;
  companyName?: string | null;
  contactEmail: string;
  contactPhone?: string | null;
  status: CompanyInvitationStatus;
  emailStatus?: 'not_sent' | 'sent' | 'failed';
  linkState?: 'active' | 'expired' | 'used' | 'revoked';
  createdAt: string;
}

const FILTER_STATUSES: CompanyInvitationStatus[] = ['pending', 'sent', 'accepted', 'rejected'];

export const formatDateTime = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch {
    return '';
  }
};

interface AdminInvitationsListProps<T extends AdminInvitationRow> {
  /**
   * i18n prefix with title, description, noInvitations, noResults, export,
   * dragToReorder, showingRange, sort.{newest,oldest} and columns.<field>.
   */
  i18nPrefix: string;
  breadcrumb: { label: string; path?: string }[];
  load: () => Promise<T[]>;
  /** Saved column order preference key. */
  preferenceKey: string;
  defaultColumnOrder: string[];
  /** Columns beyond the shared e-mail, phone, status and created ones. */
  ownColumns: ProjectsListColumn<T>[];
  /** Field the phone search and the A–Ö sort use, with the sort's label. */
  nameSort: { field: string; label: string };
  cardTitle: (row: T) => React.ReactNode;
  cardLines: (row: T) => React.ReactNode;
  exportName: string;
  testIds: { table: string; cards: string };
}

/**
 * Admin invitation list shared by contractors and clients (S8): status with
 * delivery and resend, the projects list table with saved column order, and
 * a CSV export of the visible rows.
 */
export function AdminInvitationsList<T extends AdminInvitationRow>({
  i18nPrefix,
  breadcrumb,
  load,
  preferenceKey,
  defaultColumnOrder,
  ownColumns,
  nameSort,
  cardTitle,
  cardLines,
  exportName,
  testIds
}: AdminInvitationsListProps<T>) {
  const { t } = useTranslation();
  const k = (key: string) => `${i18nPrefix}.${key}`;
  const [invitations, setInvitations] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [deliveryMessage, setDeliveryMessage] = useState<{ error: boolean; text: string } | null>(null);
  const [visibleRows, setVisibleRows] = useState<T[]>([]);
  const [columnOrder, handleColumnReorder] = useColumnOrder(preferenceKey, defaultColumnOrder);

  const resend = async (invitation: T) => {
    setResendingId(invitation.id);
    setDeliveryMessage(null);
    try {
      const updated = await adminApi.resendCompanyInvitation(invitation.id);
      setInvitations(previous => previous.map(item => item.id === invitation.id ? {
        ...item, status: updated.status, emailStatus: updated.emailStatus, linkState: updated.linkState
      } : item));
      setDeliveryMessage({ error: updated.emailStatus !== 'sent', text: t(updated.emailStatus === 'sent' ? 'companySetup.sent' : 'companySetup.notSent', { email: invitation.contactEmail }) });
    } catch {
      setDeliveryMessage({ error: true, text: t('companySetup.resendError') });
    } finally { setResendingId(null); }
  };

  const deliveryActions = (invitation: T) => <div className="company-setup mt-2">
    {invitation.emailStatus && <div className="small text-muted">{t(`companySetup.delivery.${invitation.emailStatus}`)}</div>}
    {invitation.linkState === 'expired' && <div className="small text-warning">{t('companySetup.expired')}</div>}
    {['pending', 'sent'].includes(invitation.status) && <button type="button" className="btn btn-outline-primary mt-2" disabled={resendingId !== null} onClick={(e) => { e.stopPropagation(); resend(invitation); }}>
      {t('companySetup.resend')}
    </button>}
  </div>;

  useEffect(() => {
    load()
      .then(setInvitations)
      .catch(error => {
        console.error('Failed to load data:', error);
        // Not an empty list: say that the invitations could not be loaded.
        setLoadFailed(true);
      })
      .finally(() => setIsLoading(false));
    // The loader is fixed per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusBadge = (status: CompanyInvitationStatus) => {
    switch (status) {
      case 'pending':
        return { label: t('adminCompanyInvitations.status.pending'), className: 'bg-warning text-dark' };
      case 'sent':
        return { label: t('adminCompanyInvitations.status.sent'), className: 'bg-info' };
      case 'accepted':
        return { label: t('adminCompanyInvitations.status.accepted'), className: 'bg-success' };
      case 'revoked':
        return { label: t('companySetup.revoked'), className: 'bg-secondary' };
      case 'rejected':
        return { label: t('adminCompanyInvitations.status.rejected'), className: 'bg-danger' };
      default:
        return { label: status, className: 'bg-secondary' };
    }
  };

  const statusOptions = useMemo(
    () => FILTER_STATUSES.map(value => ({ value, label: t(`adminCompanyInvitations.status.${value}`) })),
    [t]
  );

  // Memoized: the table reports its visible rows whenever the columns change,
  // and a new array every render would loop through setVisibleRows.
  const columns: ProjectsListColumn<T>[] = useMemo(() => [
    ...ownColumns,
    { field: 'contactEmail', label: t(k('columns.contactEmail')), render: i => i.contactEmail },
    { field: 'contactPhone', label: t(k('columns.contactPhone')), render: i => i.contactPhone },
    {
      field: 'status',
      label: t(k('columns.status')),
      filter: 'select',
      selectAllLabel: t('adminCompanyInvitations.allStatuses'),
      selectOptions: statusOptions,
      render: i => {
        const badge = getStatusBadge(i.status);
        return (
          <>
            <span className={`badge ${badge.className}`}>{badge.label}</span>
            {deliveryActions(i)}
          </>
        );
      }
    },
    {
      field: 'createdAt',
      label: t(k('columns.createdAt')),
      defaultSortDirection: 'desc',
      sortValue: i => (i.createdAt ? new Date(i.createdAt).getTime() : 0),
      filterValue: i => formatDateTime(i.createdAt),
      render: i => formatDateTime(i.createdAt)
    }
    // The cell renderers only depend on the language and the resend in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ownColumns, statusOptions, resendingId, t]);

  const exportToExcel = () => {
    const byField = new Map(columns.map(column => [column.field, column]));
    const order = columnOrder.filter(field => byField.has(field));
    const headers = order.map(field => byField.get(field)!.label);

    const getCellValue = (invitation: T, field: string): string => {
      if (field === 'createdAt') return formatDateTime(invitation.createdAt);
      if (field === 'status') return getStatusBadge(invitation.status).label;
      const column = byField.get(field)!;
      if (column.filterValue) return column.filterValue(invitation);
      const value = (invitation as unknown as Record<string, unknown>)[field];
      return value == null ? '' : String(value);
    };

    const rows = visibleRows.map(invitation => order.map(field => getCellValue(invitation, field)));

    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${String(cell || '').replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    const BOM = '﻿';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${exportName}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const renderMobileCard = (invitation: T) => {
    const badge = getStatusBadge(invitation.status);
    return (
      <div className="card mb-2">
        <div className="card-body py-2 px-3">
          <div className="flex-grow-1 overflow-hidden">
            <div className="d-flex align-items-center gap-2 mb-1">
              <span className="fw-semibold text-truncate">{cardTitle(invitation)}</span>
              <span className={`badge ${badge.className} flex-shrink-0`}>{badge.label}</span>
            </div>
            {cardLines(invitation)}
            {invitation.contactEmail && (
              <div className="text-muted small text-truncate"><i className="feather-mail me-1"></i>{invitation.contactEmail}</div>
            )}
            {invitation.contactPhone && (
              <div className="text-muted small"><i className="feather-phone me-1"></i>{invitation.contactPhone}</div>
            )}
            <div className="text-muted small mt-1"><i className="feather-calendar me-1"></i>{formatDateTime(invitation.createdAt)}</div>
            {deliveryActions(invitation)}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t(k('title'))}</h5>
          </div>
          <Breadcrumb items={breadcrumb} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t(k('description'))}</p>
        </div>
      </div>

      {loadFailed && <div className="alert alert-danger" role="alert">{t(k('loadError'))}</div>}
      {deliveryMessage && <div className={`alert ${deliveryMessage.error ? 'alert-warning' : 'alert-success'}`} role="status">{deliveryMessage.text}</div>}
      <Card>
        <ProjectsListTable
          rows={invitations}
          columns={columns}
          rowKey={i => i.id}
          columnOrder={columnOrder}
          onColumnReorder={handleColumnReorder}
          reorderTitle={t(k('dragToReorder'))}
          initialSortField="createdAt"
          initialSortDirection="desc"
          actionTitle=""
          emptyText={t(k('noInvitations'))}
          noResultsText={t(k('noResults'))}
          rangeText={range => t(k('showingRange'), range)}
          mobileFilter={{
            searchField: nameSort.field,
            select: { field: 'status', allLabel: t('adminCompanyInvitations.allStatuses'), options: statusOptions },
            sort: {
              label: t('projects.sort.label'),
              options: [
                { field: 'createdAt', direction: 'desc', label: t(k('sort.newest')) },
                { field: 'createdAt', direction: 'asc', label: t(k('sort.oldest')) },
                { field: nameSort.field, direction: 'asc', label: nameSort.label }
              ]
            }
          }}
          renderMobileCard={renderMobileCard}
          onVisibleRowsChange={setVisibleRows}
          testIds={testIds}
          toolbar={
            <Button variant="light-brand" className="admin-list-toolbar__btn" onClick={exportToExcel} aria-label={t(k('export'))}>
              <i className="feather-download" aria-hidden="true"></i>
              <span className="d-none d-sm-inline ms-2">{t(k('export'))}</span>
            </Button>
          }
        />
      </Card>
    </DashboardLayout>
  );
}
